import { IconType } from 'react-icons'

import { useClinic } from '~contexts/clinic-context'
import { AdminRoutes, UserRoutes } from '~config/routes'
import { useActionItems } from '~contexts/action-item-provider'
import {
    AdminPermissionFeature,
    ClinicFeatureFieldsFragment,
    LoyaltyRedemptionStatus,
    useCountLoyaltyRedemptionsQuery,
} from '~graphql/generated/graphql'
import { eqStringFilter } from '~utils/query-helpers'
import { useUnreadMessages } from '~contexts/unread-messages-provider'
import { useUserContext } from '~config/user-context'
import { getHasPermissions } from '~utils/helpers'
import { useUnreadInternalChats } from '~contexts/unread-internal-chats-provider'
import {
    FaArrowPointer,
    FaBell,
    FaBullhorn,
    FaBullseye,
    FaCalendar,
    FaChartLine,
    FaChartPie,
    FaCircleInfo,
    FaClock,
    FaCode,
    FaDollarSign,
    FaDoorOpen,
    FaEnvelope,
    FaFileSignature,
    FaFlag,
    FaGear,
    FaGift,
    FaHospital,
    FaInbox,
    FaLifeRing,
    FaMedal,
    FaMessage,
    FaMugHot,
    FaNoteSticky,
    FaPhone,
    FaRotate,
    FaRuler,
    FaStar,
    FaStore,
    FaTable,
    FaTableColumns,
    FaTerminal,
    FaToolbox,
    FaUserDoctor,
    FaUsers,
    FaKey,
    FaPenRuler,
    FaAddressBook,
    FaHospitalUser,
    FaCalculator,
    FaClipboardUser,
    FaRegCalendarCheck,
    FaClipboardCheck,
    FaBoxOpen,
    FaDatabase,
    FaPrescriptionBottle,
    FaPaw,
    FaWrench,
    FaImages,
    FaMobile,
    FaPhotoFilm,
    FaLayerGroup,
    FaLink,
    FaRankingStar,
} from 'react-icons/fa6'

export type SidebarItem = {
    label: string
    icon: IconType
    to?: string
    badge?: number
    isNew?: boolean
    pages?: SidebarItem[]
    hideIf?: boolean
    feature?: AdminPermissionFeature
    isExternal?: boolean
    otherPaths?: string[]
}

export function useSidebarContent(
    features: ClinicFeatureFieldsFragment | null | undefined,
): {
    sidebarItems: SidebarItem[]
    adminItems: SidebarItem[]
    enterpriseItems: SidebarItem[]
} {
    const { isAdmin } = useUserContext()
    const { clinic, permissions } = useClinic()
    const { unreadMessages } = useUnreadMessages()
    const { unreadInternalChats } = useUnreadInternalChats()
    const { actionItems } = useActionItems()

    const usingRooms = clinic?.settings?.does_use_rooms_to_schedule
    const usingClinicians = clinic?.settings?.does_use_clinicians_to_schedule

    const { data } = useCountLoyaltyRedemptionsQuery({
        variables: {
            where: [
                eqStringFilter('clinic', clinic?.id),
                eqStringFilter('status', LoyaltyRedemptionStatus.Pending),
            ],
        },
        skip: !clinic,
    })

    const loyaltyRequestCount = data?.countLoyaltyRedemptions || 0

    const {
        huddle_enabled: isUsingHuddle,
        messaging_enabled: isUsingChat,
        payments_enabled: isUsingPayments,
        scheduling_enabled: isUsingScheduling,
        reviews_enabled: isUsingReviews,
        loyalty_enabled: isUsingLoyalty,
        notifs_enabled: isUsingNotifs,
        forms_enabled: isUsingForms,
        help_enabled: isUsingHelp,
        messaging_enabled: isUsingMessaging,
        bulk_messaging_enabled: isUsingBulkMessaging,
        calls_enabled: isUsingCalls,
        teamchat_enabled: isUsingTeamChat,
        prescriptions_enabled: isUsingPrescriptionRefills,
        pet_selfies_enabled: isUsingPetSelfies,
    } = features || {}

    const sidebarItems: SidebarItem[] = [
        // {
        //     icon: FaHouse,
        //     label: 'Home',
        //     to: UserRoutes.Home,
        // },
        {
            icon: FaInbox,
            label: 'Inbox',
            to: UserRoutes.Chats,
            badge: unreadMessages,
            hideIf: !isUsingChat,
            feature: AdminPermissionFeature.Messages,
        },
        {
            icon: FaTableColumns,
            label: 'Whiteboard',
            to: UserRoutes.Whiteboard,
            hideIf: !isUsingChat,
            feature: AdminPermissionFeature.Whiteboard,
            isNew: Date.now() < new Date('2026-07-01').getTime(),
        },
        {
            icon: FaMugHot,
            label: 'Daily Huddle',
            to: UserRoutes.DailyHuddle,
            hideIf: !isUsingHuddle,
            feature: AdminPermissionFeature.DailyHuddle,
        },
        {
            icon: FaClock,
            label: 'Pending Actions',
            to: UserRoutes.PendingActions,
            badge: actionItems.length,
            hideIf: !isUsingScheduling,
            feature: AdminPermissionFeature.PendingActions,
        },
        {
            icon: FaMessage,
            label: 'Team Chat',
            to: UserRoutes.TeamChat,
            badge: unreadInternalChats,
            isNew: Date.now() < new Date('2025-06-20').getTime(),
            hideIf: !isUsingTeamChat,
            feature: AdminPermissionFeature.TeamChat,
        },
        {
            icon: FaChartPie,
            label: 'Analytics',
            pages: [
                {
                    icon: FaMedal,
                    label: 'Goals',
                    to: UserRoutes.Goals,
                    hideIf: !isUsingHuddle,
                    feature: AdminPermissionFeature.Goals,
                },
                {
                    icon: FaPenRuler,
                    label: 'Metrics',
                    to: '/analytics/metrics',
                    hideIf: !isUsingHuddle,
                    feature: AdminPermissionFeature.Reporting,
                    isNew: Date.now() < new Date('2025-10-15').getTime(),
                },
                {
                    icon: FaTable,
                    label: 'Reporting',
                    to: UserRoutes.ClinicReporting,
                    hideIf: !isUsingHuddle,
                    feature: AdminPermissionFeature.Reporting,
                    isNew: Date.now() < new Date('2025-10-15').getTime(),
                },
                {
                    icon: FaKey,
                    label: 'KPIs',
                    to: '/analytics/kpis',
                    feature: AdminPermissionFeature.Reporting,
                    isNew: Date.now() < new Date('2025-10-15').getTime(),
                },
                {
                    icon: FaChartLine,
                    label: 'Trends',
                    to: UserRoutes.ClinicTrends,
                    hideIf: !isUsingHuddle,
                    feature: AdminPermissionFeature.Trends,
                },
                {
                    icon: FaRankingStar,
                    label: 'Booking Sources',
                    to: UserRoutes.ClinicBookingSources,
                    hideIf: !isUsingScheduling,
                    feature: AdminPermissionFeature.Widgets,
                    isNew: Date.now() < new Date('2026-11-15').getTime(),
                },
                {
                    icon: FaBell,
                    label: 'Notification Analytics',
                    to: UserRoutes.ClinicNotificationAnalytics,
                    hideIf: !isUsingNotifs,
                    feature: AdminPermissionFeature.Notifications,
                    isNew: Date.now() < new Date('2026-11-15').getTime(),
                },
                {
                    icon: FaBullseye,
                    label: 'Initiatives',
                    to: UserRoutes.ClinicInitiatives,
                    hideIf: true,
                },
            ].filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaArrowPointer,
            label: 'Booking',
            pages: [
                {
                    icon: FaRegCalendarCheck,
                    label: 'Availability Checker',
                    to: UserRoutes.AvailabilityChecker,
                    hideIf: !isUsingScheduling,
                },
                {
                    icon: FaUserDoctor,
                    label: 'Clinicians',
                    to: UserRoutes.Clinicians,
                    hideIf: !usingClinicians,
                    feature: AdminPermissionFeature.Clinicians,
                },
                {
                    icon: FaDollarSign,
                    label: 'Payments',
                    to: UserRoutes.Payments,
                    hideIf: !isUsingPayments,
                    feature: AdminPermissionFeature.Payments,
                },
                {
                    icon: FaDoorOpen,
                    label: 'Rooms',
                    to: UserRoutes.Rooms,
                    hideIf: !usingRooms,
                    feature: AdminPermissionFeature.Rooms,
                },

                {
                    icon: FaRuler,
                    label: 'Scheduling Rules',
                    to: UserRoutes.SchedulingRules,
                    hideIf: !isUsingScheduling,
                    feature: AdminPermissionFeature.SchedulingRules,
                },
                {
                    icon: FaStore,
                    label: 'Services',
                    to: UserRoutes.Services,
                    feature: AdminPermissionFeature.Services,
                },
                {
                    icon: FaCode,
                    label: 'Widgets',
                    to: UserRoutes.Widgets,
                    hideIf: !(isUsingScheduling || isUsingChat),
                    feature: AdminPermissionFeature.Widgets,
                },
            ].filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaBell,
            label: 'Engagement',
            pages: [
                {
                    icon: FaClipboardCheck,
                    label: 'Compliance Tracker',
                    to: UserRoutes.ComplianceTracker,
                    isNew: Date.now() < new Date('2026-07-01').getTime(),
                },
                {
                    icon: FaAddressBook,
                    label: 'Audiences',
                    to: UserRoutes.Audiences,
                    hideIf: !isUsingNotifs,
                    feature: AdminPermissionFeature.ClientSegments,
                    isNew: Date.now() < new Date('2026-01-01').getTime(),
                },
                {
                    icon: FaBullhorn,
                    label: 'Bulk Messages',
                    to: UserRoutes.MassCommunication,
                    hideIf: !isUsingNotifs || !isUsingBulkMessaging,
                    feature: AdminPermissionFeature.BulkMessages,
                },
                {
                    icon: FaPhone,
                    label: 'Calls',
                    to: UserRoutes.Calls,
                    hideIf: !isUsingCalls,
                },
                {
                    icon: FaFlag,
                    label: 'Campaigns',
                    to: UserRoutes.Campaigns,
                    hideIf: new Date() < new Date('2025-01-25'),
                    feature: AdminPermissionFeature.Campaigns,
                },
                {
                    icon: FaEnvelope,
                    label: 'Email Templates',
                    to: UserRoutes.EmailTemplates,
                    hideIf: !isUsingMessaging,
                    feature: AdminPermissionFeature.EmailTemplates,
                },
                {
                    icon: FaFileSignature,
                    label: 'Forms',
                    to: UserRoutes.Forms,
                    hideIf: !isUsingForms,
                    feature: AdminPermissionFeature.Forms,
                },
                {
                    icon: FaGift,
                    label: 'Loyalty',
                    to: UserRoutes.Loyalty,
                    hideIf: !isUsingLoyalty,
                    badge: loyaltyRequestCount,
                    feature: AdminPermissionFeature.Loyalty,
                },
                {
                    icon: FaTerminal,
                    label: 'Message Logs',
                    to: UserRoutes.NotificationLogs,
                    hideIf: !isUsingNotifs,
                    feature: AdminPermissionFeature.MessageLogs,
                },
                {
                    icon: FaNoteSticky,
                    label: 'Message Templates',
                    to: UserRoutes.MessageTemplates,
                    hideIf: !isUsingMessaging,
                    feature: AdminPermissionFeature.MessageTemplates,
                },
                {
                    icon: FaBell,
                    label: 'Notifications',
                    to: UserRoutes.Notifications,
                    hideIf: !isUsingNotifs,
                    feature: AdminPermissionFeature.Notifications,
                },
                {
                    icon: FaStar,
                    label: 'Reviews',
                    to: UserRoutes.Reviews,
                    hideIf: !isUsingReviews,
                    feature: AdminPermissionFeature.Reviews,
                },
                {
                    icon: FaPhotoFilm,
                    label: 'Shared Media',
                    to: UserRoutes.SharedMedia,
                    hideIf: !isUsingMessaging,
                    feature: AdminPermissionFeature.EmailTemplates,
                    isNew: Date.now() < new Date('2026-07-15').getTime(),
                },
            ].filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaPaw,
            label: 'Patient Portal',
            pages: (
                [
                    {
                        icon: FaWrench,
                        label: 'Customization',
                        to: UserRoutes.PortalCustomization,
                        feature: AdminPermissionFeature.GeneralSettings,
                        isNew: Date.now() < new Date('2026-05-15').getTime(),
                    },
                    {
                        icon: FaChartPie,
                        label: 'User Tracking',
                        to: UserRoutes.UserTracking,
                        feature: AdminPermissionFeature.GeneralSettings,
                        isNew: Date.now() < new Date('2026-12-15').getTime(),
                    },
                    {
                        icon: FaImages,
                        label: 'Marketing Materials',
                        to: UserRoutes.MarketingMaterials,
                        feature: AdminPermissionFeature.GeneralSettings,
                        isNew: Date.now() < new Date('2026-06-15').getTime(),
                    },
                    {
                        icon: FaPhotoFilm,
                        label: 'Pet Selfies',
                        to: UserRoutes.PetSelfies,
                        feature: AdminPermissionFeature.GeneralSettings,
                        hideIf: !isUsingPetSelfies,
                        isNew: Date.now() < new Date('2026-09-15').getTime(),
                    },
                    {
                        icon: FaPrescriptionBottle,
                        label: 'Prescription Refills',
                        to: UserRoutes.PrescriptionRefillRequests,
                        hideIf: !isUsingPrescriptionRefills,
                    },
                    {
                        icon: FaMobile,
                        label: 'Open Patient Portal',
                        to:
                            process.env.NODE_ENV === 'development'
                                ? 'https://dev.portal.chckup.app'
                                : 'https://portal.chckup.app',
                        isExternal: true,
                    },
                ] as SidebarItem[]
            ).filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaHospital,
            label: 'Clinic',
            pages: [
                {
                    icon: FaCalendar,
                    label: 'Appointments',
                    to: UserRoutes.Appointments,
                    hideIf: !isUsingScheduling,
                    feature: AdminPermissionFeature.Appointments,
                },

                {
                    icon: FaClipboardUser,
                    label: 'Clients & Patients',
                    to: UserRoutes.ClientsPatients,
                    otherPaths: [
                        UserRoutes.ClientDetails(''),
                        UserRoutes.PatientDetails(''),
                    ],
                },
                {
                    icon: FaCircleInfo,
                    label: 'Clinic Info',
                    to: UserRoutes.ClinicInfo,
                    badge: clinic?.onboarding?.has_set_hours ? 0 : 1,
                    feature: AdminPermissionFeature.ClinicInfo,
                },
                {
                    icon: FaRotate,
                    label: 'Data Sync',
                    to: UserRoutes.DataSync,
                    hideIf: !isAdmin,
                },
                {
                    icon: FaUsers,
                    label: 'Users',
                    to: UserRoutes.ClinicUsers,
                    hideIf: false,
                    feature: AdminPermissionFeature.Users,
                },
            ].filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaGear,
            label: 'Settings',
            pages: [
                {
                    icon: FaGear,
                    label: 'General',
                    to: UserRoutes.GeneralSettings,
                    feature: AdminPermissionFeature.GeneralSettings,
                },
                {
                    icon: FaDollarSign,
                    label: 'Billing',
                    to: UserRoutes.BillingSettings,
                    feature: AdminPermissionFeature.GeneralSettings,
                },
                {
                    icon: FaArrowPointer,
                    label: 'Booking',
                    to: UserRoutes.BookingSettings,
                    feature: AdminPermissionFeature.BookingSettings,
                },
                {
                    icon: FaBell,
                    label: 'Engagement',
                    to: UserRoutes.EngagementSettings,
                    hideIf: false,
                    feature: AdminPermissionFeature.EngagementSettings,
                },
                {
                    icon: FaRotate,
                    label: 'Sync',
                    to: UserRoutes.SyncSettings,
                    hideIf: false,
                    feature: AdminPermissionFeature.SyncSettings,
                },
            ].filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },

        {
            icon: FaLifeRing,
            label: 'Help Center',
            to: 'https://intercom.help/chckvet',
            isExternal: true,
        },
    ].filter(
        i =>
            !i.hideIf &&
            (i.feature
                ? getHasPermissions(permissions, i.feature, isAdmin)
                : !!i?.pages
                  ? i.pages.length > 0
                  : true),
    )

    const adminItems: SidebarItem[] = [
        {
            icon: FaHospital,
            label: 'Clinics',
            to: AdminRoutes.Clinics,
        },
        {
            icon: FaHospitalUser,
            label: 'Clinic Groups',
            to: '/admin/clinic-groups',
        },
        {
            icon: FaDatabase,
            label: 'PMS DBs',
            to: AdminRoutes.PmsDbs,
        },
        {
            icon: FaUsers,
            label: 'Users',
            to: AdminRoutes.Users,
        },
        {
            icon: FaCalculator,
            label: 'Usage',
            to: AdminRoutes.Usage,
        },
        {
            icon: FaToolbox,
            label: 'Tools',
            pages: [
                {
                    icon: FaTerminal,
                    label: 'ezyVet Tools',
                    to: AdminRoutes.EzyVetTools,
                },
                {
                    icon: FaTerminal,
                    label: 'Instinct Tools',
                    to: AdminRoutes.InstinctTools,
                },
                {
                    icon: FaTerminal,
                    label: 'Bitwerx Tools',
                    to: AdminRoutes.BitwerxTools,
                },
                {
                    icon: FaTerminal,
                    label: 'Support Bot',
                    to: AdminRoutes.SupportBot,
                },
            ],
        },
    ]

    const enterpriseItems: SidebarItem[] = [
        {
            icon: FaInbox,
            label: 'Inbox',
            to: UserRoutes.Chats,
            badge: unreadMessages,
            feature: AdminPermissionFeature.Messages,
        },
        // {
        //     icon: FaTableColumns,
        //     label: 'Whiteboard',
        //     to: UserRoutes.Whiteboard,
        //     feature: AdminPermissionFeature.Whiteboard,
        //     isNew: Date.now() < new Date('2026-07-01').getTime(),
        // },
        {
            icon: FaMessage,
            label: 'Team Chat',
            to: UserRoutes.TeamChat,
            badge: unreadInternalChats,
            isNew: Date.now() < new Date('2025-06-20').getTime(),
            feature: AdminPermissionFeature.TeamChat,
        },
        {
            icon: FaChartPie,
            label: 'Analytics',
            pages: [
                {
                    icon: FaPenRuler,
                    label: 'Metrics',
                    to: '/enterprise/metrics',
                    feature: AdminPermissionFeature.GroupReporting,
                    isNew: Date.now() < new Date('2025-10-15').getTime(),
                },
                {
                    icon: FaTable,
                    label: 'Reports',
                    to: '/enterprise/reports',
                    feature: AdminPermissionFeature.GroupReporting,
                    isNew: Date.now() < new Date('2025-10-15').getTime(),
                },
                {
                    icon: FaKey,
                    label: 'KPIs',
                    to: '/enterprise/kpis',
                    feature: AdminPermissionFeature.GroupKpIs,
                    isNew: Date.now() < new Date('2025-10-15').getTime(),
                },
                {
                    icon: FaChartLine,
                    label: 'Trends',
                    to: '/enterprise/trends',
                    feature: AdminPermissionFeature.GroupTrends,
                },
                {
                    icon: FaRankingStar,
                    label: 'Booking Sources',
                    to: UserRoutes.EnterpriseBookingSources,
                    feature: AdminPermissionFeature.GroupWidgets,
                },
                {
                    icon: FaBell,
                    label: 'Notification Analytics',
                    to: UserRoutes.EnterpriseNotificationAnalytics,
                    feature: AdminPermissionFeature.GroupReporting,
                },
                {
                    icon: FaBullseye,
                    label: 'Initiatives',
                    to: '/enterprise/initiatives',
                    feature: AdminPermissionFeature.GroupInitiatives,
                    hideIf: true,
                },
            ].filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaArrowPointer,
            label: 'Booking',
            pages: [
                {
                    icon: FaCalendar,
                    label: 'Appointments',
                    to: UserRoutes.EnterpriseAppointments,
                },
                {
                    icon: FaCode,
                    label: 'Widgets',
                    to: UserRoutes.OrganizationWidgets,
                    feature: AdminPermissionFeature.GroupWidgets,
                },
            ],
        },
        // {
        //     icon: FaBullhorn,
        //     label: 'Marketing',
        //     to: UserRoutes.EnterpriseMarketing,
        //     feature: AdminPermissionFeature.GroupReporting,
        //     isNew: Date.now() < new Date('2026-07-01').getTime(),
        //     otherPaths: [UserRoutes.EnterpriseAppointments],
        //     // Hidden from the sidebar while we validate the data live with
        //     // the marketing managers. The page itself stays reachable at
        //     // /enterprise/marketing for direct testing.
        //     hideIf: true,
        // },
        {
            icon: FaBell,
            label: 'Engagement',
            pages: (
                [
                    {
                        icon: FaClipboardCheck,
                        label: 'Compliance Tracker',
                        to: UserRoutes.ComplianceTracker,
                        isNew: Date.now() < new Date('2026-07-01').getTime(),
                    },
                    {
                        icon: FaFileSignature,
                        label: 'Forms',
                        to: UserRoutes.Forms,
                        feature: AdminPermissionFeature.Forms,
                    },
                    {
                        icon: FaStar,
                        label: 'Reviews',
                        to: UserRoutes.Reviews,
                        feature: AdminPermissionFeature.GroupReporting,
                    },
                    {
                        icon: FaPhotoFilm,
                        label: 'Shared Media',
                        to: UserRoutes.SharedMedia,
                        feature: AdminPermissionFeature.EmailTemplates,
                        isNew: Date.now() < new Date('2026-07-15').getTime(),
                    },
                ] as SidebarItem[]
            ).filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaLayerGroup,
            label: 'Standardizations',
            isNew: Date.now() < new Date('2026-09-15').getTime(),
            pages: (
                [
                    {
                        icon: FaLink,
                        label: 'Clinic Coverage',
                        to: UserRoutes.EnterpriseStandardizationCoverage,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    {
                        icon: FaAddressBook,
                        label: 'Audiences',
                        to: UserRoutes.EnterpriseStandardizationAudiences,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    {
                        icon: FaBullhorn,
                        label: 'Bulk Messages',
                        to: UserRoutes.EnterpriseStandardizationBulkMessages,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    // {
                    //     icon: FaClipboardUser,
                    //     label: 'Custom Roles',
                    //     to: UserRoutes.EnterpriseStandardizationCustomRoles,
                    //     feature: AdminPermissionFeature.GroupOnboarding,
                    // },
                    {
                        icon: FaEnvelope,
                        label: 'Email Templates',
                        to: UserRoutes.EnterpriseStandardizationEmailTemplates,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    {
                        icon: FaFileSignature,
                        label: 'Forms',
                        to: UserRoutes.EnterpriseStandardizationForms,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    {
                        icon: FaCode,
                        label: 'Merge Tags',
                        to: UserRoutes.EnterpriseStandardizationMergeTags,
                        feature: AdminPermissionFeature.ClinicGroupInfo,
                        otherPaths: ['/enterprise/merge-tags'],
                    },
                    {
                        icon: FaNoteSticky,
                        label: 'Message Templates',
                        to: UserRoutes.EnterpriseStandardizationMessageTemplates,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    {
                        icon: FaBell,
                        label: 'Notifications',
                        to: UserRoutes.EnterpriseStandardizationNotifications,
                        feature: AdminPermissionFeature.GroupOnboarding,
                    },
                    {
                        icon: FaGear,
                        label: 'Shared Settings',
                        to: UserRoutes.EnterpriseSettings,
                        feature: AdminPermissionFeature.GroupOnboarding,
                        otherPaths: [
                            UserRoutes.EnterpriseBookingSettings,
                            UserRoutes.EnterpriseEngagementSettings,
                        ],
                    },
                ] as SidebarItem[]
            ).filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
        {
            icon: FaHospitalUser,
            label: 'Group',
            pages: (
                [
                    {
                        icon: FaClipboardCheck,
                        label: 'Onboarding',
                        to: '/enterprise/onboarding',
                        feature: AdminPermissionFeature.GroupOnboarding,
                        isNew: Date.now() < new Date('2026-10-01').getTime(),
                    },
                    {
                        icon: FaUsers,
                        label: 'Users',
                        to: '/enterprise/users',
                        feature: AdminPermissionFeature.GroupUsers,
                    },
                    {
                        icon: FaCircleInfo,
                        label: 'Group Info',
                        to: UserRoutes.EnterpriseGroupInfo,
                        feature: AdminPermissionFeature.ClinicGroupInfo,
                    },
                ] as SidebarItem[]
            ).filter(
                i =>
                    !i.hideIf &&
                    (i.feature
                        ? getHasPermissions(permissions, i.feature, isAdmin)
                        : true),
            ),
        },
    ].filter(i =>
        // !i.hideIf &&
        i.feature ? getHasPermissions(permissions, i.feature, isAdmin) : true,
    )

    return {
        sidebarItems,
        adminItems,
        enterpriseItems,
    }
}
